import type {
  ClozeBlank,
  FlashCard,
  GameMode,
  MCQOption,
  RemediationTarget,
} from '../types/deck';

/**
 * Event types for Phaser ↔ React communication
 */
export interface GameEvents {
  // Scene transitions
  'scene-changed': { scene: 'hub' | 'dungeon' | 'encounter' | 'judgment' | 'settings' };

  // Dungeon events
  'dungeon-entered': { dungeonId: string; subject: string; encounterCount: number };
  'dungeon-completed': { dungeonId: string; subject: string; clearedCount: number };

  // Encounter events
  'encounter-started': {
    cardId: string;
    card: FlashCard;
    question: string;
    options: MCQOption[];
    subject: string;
    isRematch: boolean;
    mode: GameMode;
    blanks?: ClozeBlank[];
    hint?: string;
    explain?: string;
    scenario?: string;
    remediation?: RemediationTarget[];
  };
  'answer-selected': { optionId?: string; blankSelections?: Record<string, string> };
  'answer-submitted': {
    cardId: string;
    selectedOptionId?: string;
    selectedOptionIds?: string[];
    correct: boolean;
    xp: number;
    coins: number;
    streak: number;
    mode: GameMode;
    blankResults?: { blankId: string; selectedOptionId?: string; correctOptionId: string }[];
  };

  // Judgment events
  'judgment-started': {
    card: FlashCard;
    wasCorrect: boolean;
    playerAnswer: string;
    correctAnswer: string;
    subject: string;
  };

  // Progress events
  'xp-gained': { amount: number; total: number };
  'coins-gained': { amount: number; total: number };
  'streak-changed': { streak: number };
  'run-progress': { current: number; total: number };

  // Run events
  'run-started': { subject: string; totalCards?: number; dungeonId?: string };
  'run-completed': { subject: string; xp: number; coins: number; correctAnswers: number; totalCards: number };

  // UI events
  'settings-toggle': { open: boolean };
  'rematch-requested': { cardId: string };
  'continue-run': {};
  'return-to-hub': {};

  // Hub events
  'door-proximity': { subject: string; near: boolean };
  'door-entered': { subject: string };
}

type EventCallback<T> = (data: T) => void;

/**
 * Event bridge for Phaser ↔ React communication
 * Uses a simple pub/sub pattern to decouple game logic from UI
 */
class GameBridge {
  private listeners = new Map<string, Set<EventCallback<any>>>();
  private lastEvents = new Map<string, any>();

  /**
   * Emit an event with data
   */
  emit<K extends keyof GameEvents>(event: K, data: GameEvents[K]): void {
    this.lastEvents.set(event, data);

    // Expose last event for E2E testing and helper introspection
    try {
      if (typeof window !== 'undefined') {
        (window as any).__LAST_GAME_EVENT = { event, data };
        if (event === 'scene-changed') {
          (window as any).__LAST_SCENE = (data as any).scene;
        }
      }
    } catch {
      // ignore
    }

    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in GameBridge listener for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Subscribe to an event
   * @returns Unsubscribe function
   */
  subscribe<K extends keyof GameEvents>(
    event: K,
    callback: EventCallback<GameEvents[K]>
  ): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  /**
   * Get the last emitted data for an event
   */
  getLastEvent<K extends keyof GameEvents>(event: K): GameEvents[K] | undefined {
    return this.lastEvents.get(event);
  }

  /**
   * Clear all listeners (useful for cleanup)
   */
  clearAll(): void {
    this.listeners.clear();
    this.lastEvents.clear();
  }

  /**
   * Clear listeners for a specific event
   */
  clear(event: keyof GameEvents): void {
    this.listeners.delete(event);
    this.lastEvents.delete(event);
  }
}

// Singleton instance
export const gameBridge = new GameBridge();
